/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {useMutation} from '@apollo/client';
import StickyHeadTable, {
  ColumnProps,
} from 'components/StickyHeadTable/StickyHeadTable';
import {useTranslation} from 'react-i18next';
import ModalController from 'controllers/ModalController';
import {Button, IconButton} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteParamModal from 'viewComponents/DeleteParamModal/DeleteParamModal';
import {ConfigurationTableProps} from 'models/configuration';
import {
  ADD_INABILITY,
  CREATE_NEW_INABILITY,
  UPDATE_INABILITY,
} from 'graphql/admin/Mutation';
import AddParamModal from 'viewComponents/AddParamModal/AddParamModal';
import GqlQueryController from 'controllers/GqlQueryController/GqlQueryController';
import {GET_INABILITY_CONFIGURATIONS} from 'graphql/absences/Querys';
import './HealthcareParamsTable.scss';
import EditIcon from '@mui/icons-material/Edit'; // 👈 Importar el ícono de editar
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';

function buildCreateVariables(values: any, country: string | undefined) {
  const inabilityName = values?.inabilityName?.trim();
  const days = parseInt(values.days, 10);
  if (!inabilityName) {
    throw new Error('Debe capturar el nombre de incapacidad');
  }
  if (!country) {
    throw new Error('No se encontró el país para crear la incapacidad');
  }
  if (days <= 0) {
    throw new Error('Los días deben ser mayor a 0');
  }
  if (isNaN(days)) {
    throw new Error('Días debe ser un número válido');
  }
  return {inabilityName, country, days};
}

function buildAddVariables(values: any, inabilityTypes: any[]) {
  const selectedInability = inabilityTypes.find(
    (type: any) => type.configId === parseInt(values.leaveType, 10)
  );
  if (!selectedInability) {
    throw new Error('Debe seleccionar una incapacidad');
  }
  return {
    id: parseInt(values.leaveType, 10),
    days: selectedInability.configDays,
    status: 'ACTIVE',
    inabilityTypeName: selectedInability.description,
  };
}

const HealthcareParamsTable: React.FC<ConfigurationTableProps> = ({
  country,
  isActive,
}) => {
  const {t} = useTranslation();
  const [updateInability] = useMutation(UPDATE_INABILITY);

  const renderSwitchController = (values: any, refetchCallback: () => void) => (
    <IconButton
      onClick={async () => {
        const nextStatus = values.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        await updateInability({
          variables: {
            id: values.id,
            days: values.numberOfDays,
            status: nextStatus,
            inabilityTypeName: values.type.description,
          },
        });
        refetchCallback();
      }}
    >
      {values.status === 'ACTIVE' ? (
        <ToggleOnIcon sx={{color: 'success.main'}} />
      ) : (
        <ToggleOffIcon />
      )}
    </IconButton>
  );

  // 👇 NUEVA FUNCIÓN: Controlador para editar
  const renderEditController = (values: any, refetchCallback: () => void) => (
    <ModalController
      trigger={({onToggleModal}) => (
        <IconButton onClick={onToggleModal}>
          <EditIcon />
        </IconButton>
      )}
      modal={({isModalOpen, onToggleModal}) => (
        <AddParamModal
          title={t('leaves.HealthcareParamsTable.editModalTitle')}
          submitButtonText={t('leaves.HealthcareParamsTable.editSubmitButton')}
          cancelButtonText={t('leaves.AddParamModal.cancel')}
          formProps={{
            controls: [
              {
                name: 'leaveType',
                type: 'select',
                options: {
                  required: true,
                },
                props: {
                  label: t('leaves.HealthcareParamsTable.type'),
                  placeholder: t('common.choose_an_option'),
                  className: 'mt-2',
                  disabled: true, // 👈 Deshabilitado porque el tipo no se puede cambiar
                  noSelection: t('common.choose_an_option'),
                  children: [
                    {
                      key: values.type.name, // 👈 Valor actual
                      description: values.type.description,
                    },
                  ],
                },
              },
              {
                name: 'days',
                options: {
                  required: true,
                },
                props: {
                  label: t('leaves.HealthcareParamsTable.days'),
                  inputProps: {type: 'number'},
                },
              },
            ],
            defaultValues: {
              leaveType: values.type.name, // 👈 Valores por defecto
              days: values.numberOfDays,
            },
            variablesMapper: (formValues: any) => {
              const days = Number(formValues.days);
              if (days <= 0) {
                throw new Error('Los días deben ser mayor a 0');
              }
              if (isNaN(days)) {
                throw new Error('Días debe ser un número válido');
              }
              return {
                id: values.id,
                days: days,
                status: values.status || 'ACTIVE',
                inabilityTypeName: values.type.description,
              };
            },
            onSubmit: async (mutation: any) => {
              await mutation;
              refetchCallback();
              onToggleModal();
            },
            mutation: UPDATE_INABILITY, // 👈 NUEVA MUTATION (crear abajo)
          }}
          onClose={onToggleModal}
          open={isModalOpen}
        />
      )}
    />
  );

  const renderDeleteController = (values: any, refetchCallback: () => void) => (
    <ModalController
      trigger={({onToggleModal}) => (
        <IconButton onClick={onToggleModal}>
          <DeleteIcon />
        </IconButton>
      )}
      modal={({isModalOpen, onToggleModal}) => (
        <DeleteParamModal
          variablesMapper={() => {
            return {
              id: values.id,
              days: values.numberOfDays,
              status: 'DELETED',
              inabilityTypeName: values.type.description,
            };
          }}
          onSubmit={refetchCallback} // 👈 Refrescar el query después de eliminar
          mutation={UPDATE_INABILITY}
          values={values}
          onClose={onToggleModal}
          open={isModalOpen}
        />
      )}
    />
  );

  return (
    <div className="HealthcareParamsTable w-full flex flex-col flex-wrap">
      <GqlQueryController
        QUERY={GET_INABILITY_CONFIGURATIONS}
        variables={{
          country: country,
        }}
        paramValidator={(_: any, current: any) => !!current?.country}
        initialState={{data: []} as any}
        rootField="getInabilityConfigurations"
      >
        {({
          data: inabilityConfigurationsResponse,
          isLoading: _isLoading,
          fetch: refetchInabilities,
        }) => {
          const inabilityConfigurations =
            inabilityConfigurationsResponse?.data || [];

          const inabilityTypes = inabilityConfigurations
            .filter((config: any) => config.status === 'CREATED')
            .map((config: any) => ({
              ...config.inabilityType,
              configId: config.id,
              configDays: config.days,
            }));

          const tableRows = inabilityConfigurations
            .filter((config: any) =>
              ['ACTIVE', 'INACTIVE'].includes(config.status)
            )
            .map((config: any) => ({
              id: config.id,
              type: {
                name: config.inabilityType.name,
                description: config.inabilityType.description,
              },
              numberOfDays: config.days,
              country: config.country,
              status: config.status,
            }));

          // 👇 Definir columnas dentro del scope para tener acceso a refetchInabilities
          const columns: ColumnProps[] = [
            {
              id: 'type',
              label: t('leaves.HealthcareParamsTable.type'),
              render: (type: any) => type.description,
            },
            {
              id: 'numberOfDays',
              label: t('leaves.HealthcareParamsTable.days'),
            },
            {
              id: 'id',
              label: t('leaves.HealthcareParamsTable.actions'),
              align: 'right',
              render: (_: string, values: any) => (
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'flex-end',
                  }}
                >
                  {renderSwitchController(values, refetchInabilities)}{' '}
                  {/* Controlador para desactivar */}
                  {renderEditController(values, refetchInabilities)}{' '}
                  {/* Controlador para editar */}
                  {renderDeleteController(values, refetchInabilities)}{' '}
                  {/* Controlador para eliminar */}
                </div>
              ),
            },
          ];

          return (
            <>
              <div className="flex flex-row justify-end gap-4 py-6">
                {/* Botón Alta de incapacidad — disabled when country is inactive (BUG-GP-001-02) */}
                <ModalController
                  trigger={({onToggleModal}) => (
                    <Button
                      variant="outlined"
                      disabled={isActive === false}
                      onClick={() => {
                        onToggleModal();
                      }}
                    >
                      {t('leaves.HealthcareParamsTable.createButtonLabel')}
                    </Button>
                  )}
                  modal={({isModalOpen, onToggleModal}) => (
                    <AddParamModal
                      title={t('leaves.HealthcareParamsTable.createModalTitle')}
                      submitButtonText={t(
                        'leaves.HealthcareParamsTable.createSubmitButton'
                      )}
                      cancelButtonText={t('leaves.AddParamModal.cancel')}
                      formProps={{
                        controls: [
                          {
                            name: 'inabilityName',
                            options: {
                              required: true,
                            },
                            props: {
                              label: t(
                                'leaves.HealthcareParamsTable.inabilityName'
                              ),
                              className: 'mt-2',
                            },
                          },
                          {
                            name: 'days',
                            options: {
                              required: true,
                            },
                            props: {
                              label: t(
                                'leaves.HealthcareParamsTable.inabilityDays'
                              ),
                              inputProps: {type: 'number', step: 1, min: 1},
                            },
                          },
                        ],
                        variablesMapper: (values: any) =>
                          buildCreateVariables(values, country),
                        onSubmit: async (mutation: any) => {
                          await mutation;
                          await refetchInabilities();
                          onToggleModal();
                        },
                        mutation: CREATE_NEW_INABILITY,
                      }}
                      onClose={onToggleModal}
                      open={isModalOpen}
                    />
                  )}
                />

                {/* Botón Agregar incapacidad (original) — disabled when country is inactive (BUG-GP-001-02) */}
                <ModalController
                  trigger={({onToggleModal}) => (
                    <Button
                      variant="outlined"
                      disabled={isActive === false}
                      onClick={() => {
                        onToggleModal();
                      }}
                    >
                      {t('leaves.HealthcareParamsTable.addButtonLabel')}
                    </Button>
                  )}
                  modal={({isModalOpen, onToggleModal}) => (
                    <AddParamModal
                      title={t('leaves.HealthcareParamsTable.addModalTitle')}
                      formProps={{
                        controls: [
                          {
                            name: 'leaveType',
                            type: 'select',
                            options: {
                              required: true,
                            },
                            props: {
                              label: t('leaves.HealthcareParamsTable.type'),
                              placeholder: t('common.choose_an_option'),
                              className: 'mt-2',
                              noSelection: t('common.choose_an_option'),
                              children: inabilityTypes.map(
                                (subLeaveType: any) => ({
                                  key: subLeaveType.configId,
                                  description: `${subLeaveType.description} (${subLeaveType.configDays} días)`,
                                  name: subLeaveType.name,
                                  configDays: subLeaveType.configDays,
                                })
                              ),
                            },
                          },
                        ],
                        variablesMapper: (values: any) =>
                          buildAddVariables(values, inabilityTypes),
                        onSubmit: async (mutation: any) => {
                          await mutation;
                          await refetchInabilities();
                          onToggleModal();
                        },
                        mutation: ADD_INABILITY,
                      }}
                      onClose={onToggleModal}
                      open={isModalOpen}
                    />
                  )}
                />
              </div>

              <StickyHeadTable
                columns={columns}
                placeholder={t('common.empty_list')}
                hidePagination
                isLoading={_isLoading}
                rows={tableRows}
                onPageChange={() => {}}
              />
            </>
          );
        }}
      </GqlQueryController>
    </div>
  );
};

export default HealthcareParamsTable;
